import { DefaultError, UseMutationResult } from '@tanstack/react-query';

import { updateHolderRequest } from '@/api/operation';
import { updateInfo } from '@/api/user';

import { useAppSelector } from '@/hooks/redux';

import { selectLoggedInUser } from '@/redux/auth/selectors';
import { selectUserHolderJointMinor } from '@/redux/user-data/selectors';

import {
  UpdateInfoRequestPayload,
  UpdateInfoResponsePayload,
} from '@/types/api/user';

import { useMutationWithUserData } from './useMutationWithUserData';
import { PepQuestionTarget } from './usePepQuestion';

type UseQuestionInfoMutationArgs = {
  onFinished?: () => void;
  target?: PepQuestionTarget;
};

export function useQuestionInfoMutation({
  onFinished,
  target = 'self',
}: UseQuestionInfoMutationArgs): UseMutationResult<
  UpdateInfoResponsePayload,
  DefaultError,
  Partial<UpdateInfoRequestPayload>
> {
  const user = useAppSelector(selectLoggedInUser);
  const holderJointMinor = useAppSelector(selectUserHolderJointMinor);

  return useMutationWithUserData<
    UpdateInfoResponsePayload,
    DefaultError,
    Partial<UpdateInfoRequestPayload>
  >({
    mutationFn: async payload => {
      const operationId = user?.operationId;
      if (target === 'minor') {
        const minorClientId = holderJointMinor?.clientId;

        if (!operationId || minorClientId === undefined) {
          throw new Error('Operation or minor holder not found');
        }

        return updateHolderRequest(operationId, minorClientId, payload);
      }

      return updateInfo(payload);
    },
    onFinished,
  });
}
